// src/components/DashboardLayout.tsx
import React, { useState, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button, Typography } from 'antd';
import { LogoutOutlined } from '@ant-design/icons';
import { Layout, Menu, Dropdown, Avatar, Flex } from 'antd';
import {
  AppstoreOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  TeamOutlined,
  CrownOutlined,
} from '@ant-design/icons';
import { Header } from 'antd/es/layout/layout';
import Sider from 'antd/es/layout/Sider';
import { isDisabled } from '@testing-library/user-event/dist/utils';

const { Title } = Typography;

interface DashboardLayoutProps {
  children: ReactNode;
  pageTitle: string;
  userName?: string;
  headerExtra?: ReactNode;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  children,
  pageTitle,
  userName,
  headerExtra,
}) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  // Determine the selected menu key based on current path
  const getSelectedKey = () => {
    if (location.pathname === '/dashboard') return '2';
    if (location.pathname.startsWith('/subscriptions')) return '3';
    return '1';
  };

  const menuItems = [
    {
      key: '1',
      label: 'Dashboard',
      icon: <AppstoreOutlined />,
      onClick: () => navigate('/dashboard'),
      disabled : true
    },
    {
      key: '2',
      label: 'View Users',
      icon: <TeamOutlined />,
      onClick: () => navigate('/dashboard'),
    },
    {
      key: '3',
      label: 'View Subscription',
      icon: <CrownOutlined />,
      disabled: location.pathname === '/dashboard', // Enable when on subscription page
    }
  ];

  const userMenuItems = [
    {
      key: '1',
      label: 'Logout',
      icon: <LogoutOutlined />,
      onClick: logout,
    },
  ];

  // Get user initials for avatar
  const getUserInitial = () => {
    if (userName) return userName.charAt(0);
    if (user?.displayName) return user.displayName.charAt(0);
    if (user?.email) return user.email.charAt(0);
    return 'U';
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        onCollapse={(value) => setCollapsed(value)}
        className="leftSider"
      >
        <h1 className="siteLogo">
          <span className="logoFull">Switchr</span>
          <span className="logoIcon">S</span>
        </h1>
        <Menu
          theme="dark"
          selectedKeys={[getSelectedKey()]}
          mode="inline"
          items={menuItems}
          className="sidebarMenu"
        />
        <Flex align="center" className="menuFooter">
          <Dropdown
            rootClassName="dropdown"
            menu={{ items: userMenuItems }}
            trigger={['click']}
            overlayStyle={{ minWidth: 160 }}
          >
            <Avatar size={40} style={{ backgroundColor: '#4fd1c5' }}>
              {getUserInitial()}
            </Avatar>
          </Dropdown>
        </Flex>
      </Sider>

      <Layout>
        <Header className="headerSec">
          <div className="headerSecInner">
            <Title level={2} className="my-0 pageTitle">
              {pageTitle}
            </Title>
            {headerExtra}
          </div>
          <Button
            className="toggleSiderBtn"
            onClick={() => setCollapsed(!collapsed)}
            type="text"
            ghost
          >
            {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          </Button>
        </Header>

        {children}
      </Layout>
    </Layout>
  );
};

export default DashboardLayout;